import {
  PRIORITY_TYPES,
  URGENT_ASSIGN_TYPES,
  NORMAL_TYPES,
  URGENT_TYPES,
  PAST_TYPES,
  NORMAL_TYPE_HELPERS,
  URGENT_TYPE_HELPERS,
  PAST_TYPES_HELPERS,
  READABLE_BOOKING_STATUSES,
} from '@/utils/bookingTypes/constants';
import { ORDER_STATUSES, PREPAY_TYPES, PREPAY_METHODS } from '@/utils/Constants';

export function getLateCancelTransactions(claims) {
  return claims
    .filter(claim => claim.status === 'completed' && claim.decree.type === 'late_cancel')
    .map(claim => claim.decree.transactions.filter(({ target, type, status }) =>
      target === 'client' && type === 'penalty' && status === 'completed'));
}

export function getPriorityType(booking) {
  return booking.contract.isUrgent
    ? PRIORITY_TYPES.URGENT
    : PRIORITY_TYPES.NORMAL;
}

export function isPayToDriverWithoutCard(typeObject) {
  return typeObject.type === NORMAL_TYPES.PAY_DRIVER_NOCARD_FREE_CANCEL
    || typeObject.type === NORMAL_TYPES.PAY_DRIVER_NOCARD_REFUND_CANCEL;
}

export function isCurrent(status) {
  return [
    ORDER_STATUSES.CONFIRMATION_WAITING,
    ORDER_STATUSES.CONFIRMED,
    ORDER_STATUSES.READY,
    ORDER_STATUSES.REFUSE,
  ].includes(status);
}

export function getBookingAssignType(status) {
  return status === ORDER_STATUSES.READY
    ? URGENT_ASSIGN_TYPES.ASSIGNED
    : URGENT_ASSIGN_TYPES.NOTASSIGNED;
}

/**
 * Для canceled заказов
 * если есть закрытые претензии с типом late_cancel
 * считаем платной отменой
 * @param {Boolean} claims true - платная отмена
 */
function isFreeCancellationPastType(claims) {
  return getLateCancelTransactions(claims).length === 0;
}

export function isRefundCancel(type, contract) {
  switch (type) {
    default:
      return !!contract.cancellationPenaltyAmount;
  }
}

/**
 * @param booking {{prepayType: string, prepayMethod: string}}
 * @returns {boolean|boolean}
 */
export function isBalanceType(booking) {
  return booking.prepayType === PREPAY_TYPES.FULL
    && booking.prepayMethod === PREPAY_METHODS.BALANCE;
}

/**
 * @param booking {{prepayType: string, prepayMethod: string}}
 * @returns {boolean|boolean}
 */
export function isPartialBalanceType(booking) {
  return booking.prepayType === PREPAY_TYPES.PARTIAL
    && booking.prepayMethod === PREPAY_METHODS.BALANCE;
}

export function getUrgentAssignedType(booking) {
  if (isBalanceType(booking)) {
    return [
      URGENT_TYPES.PAY_BALANCE_ASSIGNED_FREE_CANCEL,
      URGENT_TYPES.PAY_BALANCE_ASSIGNED_REFUND_CANCEL,
    ];
  }
  if (booking.prepayType === PREPAY_TYPES.FULL && booking.prepayMethod !== null) {
    return [
      URGENT_TYPES.PAY_CARD_ASSIGNED_FREE_CANCEL,
      URGENT_TYPES.PAY_CARD_ASSIGNED_REFUND_CANCEL,
    ];
  }
  if (isPartialBalanceType(booking)) {
    return [
      URGENT_TYPES.PAY_PARTIAL_BALANCE_ASSIGNED_FREE_CANCEL,
      URGENT_TYPES.PAY_PARTIAL_BALANCE_ASSIGNED_REFUND_CANCEL,
    ];
  }
  if (booking.prepayType === PREPAY_TYPES.PARTIAL && booking.prepayMethod !== null) {
    return [
      URGENT_TYPES.PAY_PARTIAL_ASSIGNED_FREE_CANCEL,
      URGENT_TYPES.PAY_PARTIAL_ASSIGNED_REFUND_CANCEL,
    ];
  }
  return [
    URGENT_TYPES.PAY_DRIVER_NOCARD_ASSIGNED_FREE_CANCEL,
    URGENT_TYPES.PAY_DRIVER_NOCARD_ASSIGNED_REFUND_CANCEL,
  ];
}

export function getUrgentNotAssignedType(booking) {
  if (isBalanceType(booking)) {
    return [
      URGENT_TYPES.PAY_BALANCE_NOTASSIGNED,
      URGENT_TYPES.PAY_BALANCE_NOTASSIGNED,
    ];
  }
  if (booking.prepayType === PREPAY_TYPES.FULL) {
    return [
      URGENT_TYPES.PAY_CARD_NOTASSIGNED,
      URGENT_TYPES.PAY_CARD_NOTASSIGNED,
    ];
  }
  if (isPartialBalanceType(booking)) {
    return [
      URGENT_TYPES.PAY_PARTIAL_BALANCE_NOTASSIGNED,
      URGENT_TYPES.PAY_PARTIAL_BALANCE_NOTASSIGNED,
    ];
  }
  if (booking.prepayType === PREPAY_TYPES.PARTIAL) {
    return [
      URGENT_TYPES.PAY_PARTIAL_NOTASSIGNED,
      URGENT_TYPES.PAY_PARTIAL_NOTASSIGNED,
    ];
  }
  return [
    URGENT_TYPES.PAY_DRIVER_NOTASSIGNED,
    URGENT_TYPES.PAY_DRIVER_NOTASSIGNED,
  ];
}

export function getNormalType(booking) {
  if (isBalanceType(booking)) {
    return [
      NORMAL_TYPES.PAY_BALANCE_FREE_CANCEL,
      NORMAL_TYPES.PAY_BALANCE_REFUND_CANCEL,
    ];
  }
  if (booking.prepayType === PREPAY_TYPES.FULL && booking.prepayMethod !== null) {
    return [
      NORMAL_TYPES.PAY_CARD_FREE_CANCEL,
      NORMAL_TYPES.PAY_CARD_REFUND_CANCEL,
    ];
  }
  if (isPartialBalanceType(booking)) {
    return [
      NORMAL_TYPES.PAY_PARTIAL_BALANCE_FREE_CANCEL,
      NORMAL_TYPES.PAY_PARTIAL_BALANCE_REFUND_CANCEL,
    ];
  }
  if (booking.prepayType === PREPAY_TYPES.PARTIAL && booking.prepayMethod !== null) {
    return [
      NORMAL_TYPES.PAY_PARTIAL_FREE_CANCEL,
      NORMAL_TYPES.PAY_PARTIAL_REFUND_CANCEL,
    ];
  }
  if (booking.prepayType === PREPAY_TYPES.NONE && booking.prepayMethod !== null) {
    return [
      NORMAL_TYPES.PAY_DRIVER_CARD_FREE_CANCEL,
      NORMAL_TYPES.PAY_DRIVER_CARD_REFUND_CANCEL,
    ];
  }
  return [
    NORMAL_TYPES.PAY_DRIVER_NOCARD_FREE_CANCEL,
    NORMAL_TYPES.PAY_DRIVER_NOCARD_REFUND_CANCEL,
  ];
}

export function getUrgentType(booking) {
  const urgentType = getBookingAssignType(booking.status);
  /**  Назначен */
  if (urgentType === URGENT_ASSIGN_TYPES.ASSIGNED) {
    return getUrgentAssignedType(booking);
  }
  /** Неназначенные */
  return getUrgentNotAssignedType(booking);
}

export function getHelpersType(booking) {
  const prType = getPriorityType(booking);
  const isRefund = isRefundCancel(prType, booking.contract);
  const paymentTypes = prType === PRIORITY_TYPES.NORMAL
    ? getNormalType(booking)
    : getUrgentType(booking);
  const currentPaymentType = isRefund ? paymentTypes[1] : paymentTypes[0];
  return (withInsurance, hoursBeforeStart) => ({
    type: currentPaymentType,
    texts: prType === PRIORITY_TYPES.NORMAL
      ? NORMAL_TYPE_HELPERS[currentPaymentType](withInsurance, hoursBeforeStart)
      : URGENT_TYPE_HELPERS[currentPaymentType](withInsurance, hoursBeforeStart),
  });
}

export function getHelpersTypePast(booking) {
  switch (booking.status) {
    case ORDER_STATUSES.COMPLETED:
      if (isBalanceType(booking)) {
        return {
          type: PAST_TYPES.PAY_BALANCE_COMPLETED,
          texts: PAST_TYPES_HELPERS[PAST_TYPES.PAY_BALANCE_COMPLETED],
        };
      }
      if (booking.prepayType === PREPAY_TYPES.FULL && booking.prepayMethod !== null) {
        return {
          type: PAST_TYPES.PAY_CARD_COMPLETED,
          texts: PAST_TYPES_HELPERS[PAST_TYPES.PAY_CARD_COMPLETED],
        };
      }
      if (isPartialBalanceType(booking)) {
        return {
          type: PAST_TYPES.PAY_PARTIAL_BALANCE_COMPLETED,
          texts: PAST_TYPES_HELPERS[PAST_TYPES.PAY_PARTIAL_BALANCE_COMPLETED],
        };
      }
      if (booking.prepayType === PREPAY_TYPES.PARTIAL && booking.prepayMethod !== null) {
        return {
          type: PAST_TYPES.PAY_PARTIAL_COMPLETED,
          texts: PAST_TYPES_HELPERS[PAST_TYPES.PAY_PARTIAL_COMPLETED],
        };
      }
      if (booking.prepayType === PREPAY_TYPES.NONE && booking.prepayMethod !== null) {
        return {
          type: PAST_TYPES.PAY_DRIVER_CARD_COMPLETED,
          texts: PAST_TYPES_HELPERS[PAST_TYPES.PAY_DRIVER_CARD_COMPLETED],
        };
      }
      return {
        type: PAST_TYPES.PAY_DRIVER_NOCARD_COMPLETED,
        texts: PAST_TYPES_HELPERS[PAST_TYPES.PAY_DRIVER_NOCARD_COMPLETED],
      };
    case ORDER_STATUSES.CANCELLED:
      if (isBalanceType(booking)) {
        const fullType = isFreeCancellationPastType(booking.claims)
          ? PAST_TYPES.PAY_BALANCE_FREE_CANCELED
          : PAST_TYPES.PAY_BALANCE_REFUND_CANCELED;
        return {
          type: fullType,
          texts: PAST_TYPES_HELPERS[fullType],
        };
      }
      if (booking.prepayType === PREPAY_TYPES.FULL && booking.prepayMethod !== null) {
        const fullType = isFreeCancellationPastType(booking.claims)
          ? PAST_TYPES.PAY_CARD_FREE_CANCELED
          : PAST_TYPES.PAY_CARD_REFUND_CANCELED;
        return {
          type: fullType,
          texts: PAST_TYPES_HELPERS[fullType],
        };
      }
      if (isPartialBalanceType(booking)) {
        const partType = isFreeCancellationPastType(booking.claims)
          ? PAST_TYPES.PAY_PARTIAL_BALANCE_FREE_CANCELED
          : PAST_TYPES.PAY_PARTIAL_BALANCE_REFUND_CANCELED;
        return {
          type: partType,
          texts: PAST_TYPES_HELPERS[partType],
        };
      }
      if (booking.prepayType === PREPAY_TYPES.PARTIAL && booking.prepayMethod !== null) {
        const partType = isFreeCancellationPastType(booking.claims)
          ? PAST_TYPES.PAY_PARTIAL_FREE_CANCELED
          : PAST_TYPES.PAY_PARTIAL_REFUND_CANCELED;
        return {
          type: partType,
          texts: PAST_TYPES_HELPERS[partType],
        };
      }
      if (booking.prepayType === PREPAY_TYPES.NONE && booking.prepayMethod !== null) {
        const partType = isFreeCancellationPastType(booking.claims)
          ? PAST_TYPES.PAY_DRIVER_CARD_FREE_CANCELED
          : PAST_TYPES.PAY_DRIVER_CARD_REFUND_CANCELED;
        return {
          type: partType,
          texts: PAST_TYPES_HELPERS[partType],
        };
      }
      return {
        type: !isRefundCancel(getPriorityType(booking), booking.contract)
          ? PAST_TYPES.PAY_DRIVER_NOCARD_FREE_CANCELED
          : PAST_TYPES.PAY_DRIVER_NOCARD_REFUND_CANCELED,
        texts: PAST_TYPES_HELPERS[!isRefundCancel(getPriorityType(booking), booking.contract)
          ? PAST_TYPES.PAY_DRIVER_NOCARD_FREE_CANCELED
          : PAST_TYPES.PAY_DRIVER_NOCARD_REFUND_CANCELED],
      };
    default:
      if (isBalanceType(booking)) {
        return {
          type: PAST_TYPES.PAY_BALANCE_FAILED,
          texts: PAST_TYPES_HELPERS[PAST_TYPES.PAY_BALANCE_FAILED],
        };
      }
      if (booking.prepayType === PREPAY_TYPES.FULL && booking.prepayMethod !== null) {
        return {
          type: PAST_TYPES.PAY_CARD_FAILED,
          texts: PAST_TYPES_HELPERS[PAST_TYPES.PAY_CARD_FAILED],
        };
      }
      if (isPartialBalanceType(booking)) {
        return {
          type: PAST_TYPES.PAY_PARTIAL_BALANCE_FAILED,
          texts: PAST_TYPES_HELPERS[PAST_TYPES.PAY_PARTIAL_BALANCE_FAILED],
        };
      }
      if (booking.prepayType === PREPAY_TYPES.PARTIAL && booking.prepayMethod !== null) {
        return {
          type: PAST_TYPES.PAY_PARTIAL_FAILED,
          texts: PAST_TYPES_HELPERS[PAST_TYPES.PAY_PARTIAL_FAILED],
        };
      }
      if (booking.prepayType === PREPAY_TYPES.NONE && booking.prepayMethod !== null) {
        return {
          type: PAST_TYPES.PAY_DRIVER_CARD_FAILED,
          texts: PAST_TYPES_HELPERS[PAST_TYPES.PAY_DRIVER_CARD_FAILED],
        };
      }
      return {
        type: PAST_TYPES.PAY_DRIVER_NOCARD_FAILED,
        texts: PAST_TYPES_HELPERS[PAST_TYPES.PAY_DRIVER_NOCARD_FAILED],
      };
  }
}

export function getTypeHelper(booking, withInsurance, hoursBeforeStart) {
  if (isCurrent(booking.status)) {
    return getHelpersType(booking)(withInsurance, hoursBeforeStart);
  }
  return getHelpersTypePast(booking);
}

export function getReadableBookingStatus(type, bookingStatus) {
  // READABLE_BOOKING_STATUSES
  switch (type) {
    case URGENT_TYPES.PAY_CARD_NOTASSIGNED:
    case URGENT_TYPES.PAY_PARTIAL_NOTASSIGNED:
    case URGENT_TYPES.PAY_DRIVER_NOTASSIGNED:
    case URGENT_TYPES.PAY_BALANCE_NOTASSIGNED:
    case URGENT_TYPES.PAY_PARTIAL_BALANCE_NOTASSIGNED:
      return READABLE_BOOKING_STATUSES.URGENT_NOTASSIGNED[bookingStatus];
    case URGENT_TYPES.PAY_CARD_ASSIGNED_FREE_CANCEL:
    case URGENT_TYPES.PAY_CARD_ASSIGNED_REFUND_CANCEL:
    case URGENT_TYPES.PAY_PARTIAL_ASSIGNED_FREE_CANCEL:
    case URGENT_TYPES.PAY_PARTIAL_ASSIGNED_REFUND_CANCEL:
    case URGENT_TYPES.PAY_DRIVER_NOCARD_ASSIGNED_FREE_CANCEL:
    case URGENT_TYPES.PAY_DRIVER_NOCARD_ASSIGNED_REFUND_CANCEL:
    case URGENT_TYPES.PAY_BALANCE_ASSIGNED_FREE_CANCEL:
    case URGENT_TYPES.PAY_BALANCE_ASSIGNED_REFUND_CANCEL:
    case URGENT_TYPES.PAY_PARTIAL_BALANCE_ASSIGNED_FREE_CANCEL:
    case URGENT_TYPES.PAY_PARTIAL_BALANCE_ASSIGNED_REFUND_CANCEL:
      return READABLE_BOOKING_STATUSES.URGENT_ASSIGNED;
    case NORMAL_TYPES.PAY_CARD_FREE_CANCEL:
    case NORMAL_TYPES.PAY_CARD_REFUND_CANCEL:
    case NORMAL_TYPES.PAY_PARTIAL_FREE_CANCEL:
    case NORMAL_TYPES.PAY_PARTIAL_REFUND_CANCEL:
    case NORMAL_TYPES.PAY_DRIVER_CARD_FREE_CANCEL:
    case NORMAL_TYPES.PAY_DRIVER_CARD_REFUND_CANCEL:
    case NORMAL_TYPES.PAY_DRIVER_NOCARD_FREE_CANCEL:
    case NORMAL_TYPES.PAY_DRIVER_NOCARD_REFUND_CANCEL:
    case NORMAL_TYPES.PAY_BALANCE_FREE_CANCEL:
    case NORMAL_TYPES.PAY_BALANCE_REFUND_CANCEL:
    case NORMAL_TYPES.PAY_PARTIAL_BALANCE_FREE_CANCEL:
    case NORMAL_TYPES.PAY_PARTIAL_BALANCE_REFUND_CANCEL:
      return READABLE_BOOKING_STATUSES.NORMAL[bookingStatus];
    case PAST_TYPES.PAY_CARD_COMPLETED:
    case PAST_TYPES.PAY_PARTIAL_COMPLETED:
    case PAST_TYPES.PAY_DRIVER_CARD_COMPLETED:
    case PAST_TYPES.PAY_DRIVER_NOCARD_COMPLETED:
    case PAST_TYPES.PAY_BALANCE_COMPLETED:
    case PAST_TYPES.PAY_PARTIAL_BALANCE_COMPLETED:
      return READABLE_BOOKING_STATUSES.COMPLETED;
    case PAST_TYPES.PAY_CARD_FREE_CANCELED:
    case PAST_TYPES.PAY_CARD_REFUND_CANCELED:
    case PAST_TYPES.PAY_PARTIAL_FREE_CANCELED:
    case PAST_TYPES.PAY_PARTIAL_REFUND_CANCELED:
    case PAST_TYPES.PAY_CARD_URGENT_FREE_CANCELED:
    case PAST_TYPES.PAY_CARD_URGENT_REFUND_CANCELED:
    case PAST_TYPES.PAY_PARTIAL_URGENT_FREE_CANCELED:
    case PAST_TYPES.PAY_PARTIAL_URGENT_REFUND_CANCELED:
    case PAST_TYPES.PAY_DRIVER_CARD_FREE_CANCELED:
    case PAST_TYPES.PAY_DRIVER_CARD_REFUND_CANCELED:
    case PAST_TYPES.PAY_DRIVER_NOCARD_FREE_CANCELED:
    case PAST_TYPES.PAY_DRIVER_NOCARD_REFUND_CANCELED:
    case PAST_TYPES.PAY_BALANCE_FREE_CANCELED:
    case PAST_TYPES.PAY_BALANCE_REFUND_CANCELED:
    case PAST_TYPES.PAY_PARTIAL_BALANCE_FREE_CANCELED:
    case PAST_TYPES.PAY_PARTIAL_BALANCE_REFUND_CANCELED:
    case PAST_TYPES.PAY_PARTIAL_BALANCE_URGENT_FREE_CANCELED:
    case PAST_TYPES.PAY_PARTIAL_BALANCE_URGENT_REFUND_CANCELED:
      return READABLE_BOOKING_STATUSES.CANCELED;
    default:
      return READABLE_BOOKING_STATUSES.FAILED;
  }
}

export function getSummaryStrings(type) {
  switch (type) {
    case NORMAL_TYPES.PAY_CARD_FREE_CANCEL:
    case NORMAL_TYPES.PAY_CARD_REFUND_CANCEL:
    case URGENT_TYPES.PAY_CARD_ASSIGNED_FREE_CANCEL:
    case URGENT_TYPES.PAY_CARD_ASSIGNED_REFUND_CANCEL:
    case URGENT_TYPES.PAY_CARD_NOTASSIGNED:
      return {
        orderTotal: 'total_sum_summary_cc',
        orderTotalMetaActive: true,
        orderTotalMeta: i18n => date =>
          `${i18n('full_paid_by_card_summary_cc')} ${date}`,
        payToDriver: '',
        payToDriverMeta: '',
      };
    case NORMAL_TYPES.PAY_PARTIAL_FREE_CANCEL:
    case NORMAL_TYPES.PAY_PARTIAL_REFUND_CANCEL:
    case URGENT_TYPES.PAY_PARTIAL_ASSIGNED_FREE_CANCEL:
    case URGENT_TYPES.PAY_PARTIAL_ASSIGNED_REFUND_CANCEL:
    case URGENT_TYPES.PAY_PARTIAL_NOTASSIGNED:
      return {
        orderTotal: 'total_sum_summary_cc',
        orderTotalMetaActive: true,
        orderTotalMeta: i18n => date =>
          `${i18n('part_paid_by_card_summary_cc')} ${date}`,
        payToDriver: 'cash_to_driver_part_cc',
        payToDriverMeta: 'prepare_cash_to_driver_cc',
      };
    case NORMAL_TYPES.PAY_DRIVER_CARD_FREE_CANCEL:
    case NORMAL_TYPES.PAY_DRIVER_CARD_REFUND_CANCEL:
      return {
        orderTotal: 'total_sum_summary_cc',
        orderTotalMetaActive: false,
        orderTotalMeta: '',
        payToDriver: 'cash_to_driver_part_cc',
        payToDriverMeta: 'prepare_cash_to_driver_cc',
      };
    case NORMAL_TYPES.PAY_DRIVER_NOCARD_FREE_CANCEL:
    case NORMAL_TYPES.PAY_DRIVER_NOCARD_REFUND_CANCEL:
    case URGENT_TYPES.PAY_DRIVER_NOCARD_ASSIGNED_FREE_CANCEL:
    case URGENT_TYPES.PAY_DRIVER_NOCARD_ASSIGNED_REFUND_CANCEL:
    case URGENT_TYPES.PAY_DRIVER_NOTASSIGNED:
      return {
        orderTotal: 'total_sum_summary_cc',
        orderTotalMetaActive: false,
        orderTotalMeta: '',
        payToDriver: 'cash_to_driver_part_cc',
        payToDriverMeta: 'prepare_cash_to_driver_cc',
      };
    case NORMAL_TYPES.PAY_BALANCE_FREE_CANCEL:
    case NORMAL_TYPES.PAY_BALANCE_REFUND_CANCEL:
    case URGENT_TYPES.PAY_BALANCE_ASSIGNED_FREE_CANCEL:
    case URGENT_TYPES.PAY_BALANCE_ASSIGNED_REFUND_CANCEL:
    case URGENT_TYPES.PAY_BALANCE_NOTASSIGNED:
      return {
        orderTotal: 'total_sum_summary_cc',
        orderTotalMetaActive: false,
        orderTotalMeta: '',
        payToDriver: '',
        payToDriverMeta: '',
      };

    case NORMAL_TYPES.PAY_PARTIAL_BALANCE_FREE_CANCEL:
    case NORMAL_TYPES.PAY_PARTIAL_BALANCE_REFUND_CANCEL:
    case URGENT_TYPES.PAY_PARTIAL_BALANCE_ASSIGNED_FREE_CANCEL:
    case URGENT_TYPES.PAY_PARTIAL_BALANCE_ASSIGNED_REFUND_CANCEL:
    case URGENT_TYPES.PAY_PARTIAL_BALANCE_NOTASSIGNED:
      return {
        orderTotal: 'total_sum_summary_cc',
        orderTotalMetaActive: false,
        orderTotalMeta: '',
        payToDriver: 'cash_to_driver_part_cc',
        payToDriverMeta: 'prepare_cash_to_driver_cc',
      };

    case PAST_TYPES.PAY_CARD_COMPLETED:
    case PAST_TYPES.PAY_CARD_FAILED:
    case PAST_TYPES.PAY_CARD_FREE_CANCELED:
    case PAST_TYPES.PAY_CARD_REFUND_CANCELED:
    case PAST_TYPES.PAY_CARD_URGENT_FREE_CANCELED:
    case PAST_TYPES.PAY_CARD_URGENT_REFUND_CANCELED:
      return {
        orderTotal: 'total_sum_summary_cc',
        orderTotalMetaActive: true,
        orderTotalMeta: i18n => date =>
          `${i18n('full_paid_by_card_summary_cc')} ${date}`,
        payToDriver: '',
        payToDriverMeta: '',
      };
    case PAST_TYPES.PAY_PARTIAL_COMPLETED:
      return {
        orderTotal: 'total_sum_summary_cc',
        orderTotalMetaActive: true,
        orderTotalMeta: i18n => paidToDrover =>
          `${i18n('paid_cash_to_driver_summary_cc')} ${paidToDrover}`,
        payToDriver: '',
        payToDriverMeta: '',
      };
    case PAST_TYPES.PAY_DRIVER_CARD_COMPLETED:
    case PAST_TYPES.PAY_DRIVER_NOCARD_COMPLETED:
      return {
        orderTotal: 'total_sum_summary_cc',
        orderTotalMetaActive: true,
        orderTotalMeta: i18n => () =>
          `${i18n('paid_cash_to_driver_summary_cc')}`,
        payToDriver: '',
        payToDriverMeta: '',
      };
    case PAST_TYPES.PAY_PARTIAL_FAILED:
    case PAST_TYPES.PAY_PARTIAL_FREE_CANCELED:
    case PAST_TYPES.PAY_PARTIAL_REFUND_CANCELED:
    case PAST_TYPES.PAY_PARTIAL_URGENT_FREE_CANCELED:
    case PAST_TYPES.PAY_PARTIAL_URGENT_REFUND_CANCELED:
      return {
        orderTotal: 'total_sum_summary_cc',
        orderTotalMetaActive: true,
        orderTotalMeta: i18n => date =>
          `${i18n('part_paid_by_card_summary_cc')} ${date}`,
        payToDriver: '',
        payToDriverMeta: '',
      };
    case PAST_TYPES.PAY_BALANCE_COMPLETED:
    case PAST_TYPES.PAY_BALANCE_FAILED:
    case PAST_TYPES.PAY_BALANCE_FREE_CANCELED:
    case PAST_TYPES.PAY_BALANCE_REFUND_CANCELED:
    case PAST_TYPES.PAY_BALANCE_URGENT_FREE_CANCELED:
    case PAST_TYPES.PAY_BALANCE_URGENT_REFUND_CANCELED:
      return {
        orderTotal: 'total_sum_summary_cc',
        orderTotalMetaActive: false,
        orderTotalMeta: '',
        payToDriver: '',
        payToDriverMeta: '',
      };
    case PAST_TYPES.PAY_PARTIAL_BALANCE_COMPLETED:
      return {
        orderTotal: 'total_sum_summary_cc',
        orderTotalMetaActive: true,
        orderTotalMeta: i18n => paidToDrover =>
          `${i18n('paid_cash_to_driver_summary_cc')} ${paidToDrover}`,
        payToDriver: '',
        payToDriverMeta: '',
      };
    case PAST_TYPES.PAY_PARTIAL_BALANCE_FAILED:
    case PAST_TYPES.PAY_PARTIAL_BALANCE_FREE_CANCELED:
    case PAST_TYPES.PAY_PARTIAL_BALANCE_REFUND_CANCELED:
    case PAST_TYPES.PAY_PARTIAL_BALANCE_URGENT_FREE_CANCELED:
    case PAST_TYPES.PAY_PARTIAL_BALANCE_URGENT_REFUND_CANCELED:
      return {
        orderTotal: 'total_sum_summary_cc',
        orderTotalMetaActive: false,
        orderTotalMeta: '',
        payToDriver: '',
        payToDriverMeta: '',
      };
    default:
      return {
        orderTotal: 'total_sum_summary_cc',
        orderTotalMeta: '',
        payToDriver: '',
        payToDriverMEta: '',
      };
  }
}

export function hasCardRemains(order) {
  return order.prepayRemainedMainAmount > 0;
}



// WEBPACK FOOTER //
// ./src/utils/bookingTypes/resolvers.js